import Button from "../components/ui/Button";
import React, { useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/store";
import { logout, setAuthorization, updateSessionAdmin } from "../redux/authSlice";
import { isTokenCurrent } from "../utils/tokenUtils";
import { getAuthorization } from "../services/authService";
import { getAdminProfile } from "../services/adminProfileService";
import { useAccess } from "../hooks/useAccess";
import PageLoader from "./PageLoader";
import Layout from "./Layout/Layout";
import { refreshAdminSession } from "../services/apiClient";
import { getApiErrorMessage } from "../utils/apiError";

export default function ProtectedRoute() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { accessToken, refreshToken, authorization } = useSelector((state: RootState) => state.auth);
  const access = useAccess();
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const check = () => {
      if (isTokenCurrent(accessToken)) return;
      if (!isTokenCurrent(refreshToken)) { dispatch(logout()); return; }
      refreshAdminSession().catch(err => setError(getApiErrorMessage(err, "Unable to renew your session. Please retry.")));
    };
    check();
    const timer = setInterval(check, 15000);
    return () => clearInterval(timer);
  }, [accessToken, refreshToken, dispatch]);
  useEffect(() => {
    if (!isTokenCurrent(accessToken) && !isTokenCurrent(refreshToken)) return;
    const controller = new AbortController();
    setError("");
    (async () => {
      try {
        const [context, profile] = await Promise.all([getAuthorization(controller.signal), getAdminProfile(controller.signal)]);
        if (controller.signal.aborted) return;
        dispatch(updateSessionAdmin({ ...profile, role: profile.role?.name || "", avatarUrl: profile.profilePicture }));
        dispatch(setAuthorization(context));
      } catch {
        if (!controller.signal.aborted) setError("Unable to load your account access. Please retry.");
      }
    })();
    return () => controller.abort();
  }, [accessToken, refreshToken, dispatch, attempt]);
  if (!isTokenCurrent(accessToken) && !isTokenCurrent(refreshToken)) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (error) return <div className="page-error" role="alert">{error} <Button onClick={() => setAttempt(value => value + 1)}>Retry</Button><Button onClick={() => dispatch(logout())}>Return to sign in</Button></div>;
  if (!authorization) return <PageLoader message="Loading your account access..." />;
  if (location.pathname === "/") return <Navigate to={access.landingPage} replace />;
  if (!access.canVisit(location.pathname)) return <Layout pageTitle="Access denied"><div role="alert"><h2>Access denied</h2><p>Your role does not have access to this page.</p><Link to={access.landingPage}>Go to your workspace</Link></div></Layout>;
  return <Outlet />;
}
